import React, { useCallback, useMemo } from 'react';
import { useCurrentScale } from 'remotion';
import { ElementPositions } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/CompositionConfig';
import { SELECTION_OUTLINE_COLOR } from '@goldcast/player-config/src/constants/Remotion.constants';

const HANDLE_SIZE = 8;

export type ResizeHandleType =
  | 'top'
  | 'top-right'
  | 'right'
  | 'bottom-right'
  | 'bottom'
  | 'bottom-left'
  | 'left'
  | 'top-left';

export const ResizeHandle: React.FC<{
  positions: ElementPositions;
  type: ResizeHandleType;
  minimumWidthPercentage?: number;
  outlineRef: React.RefObject<HTMLDivElement>;
  updatePositions: (newPositions: ElementPositions) => void;
  onHandleRelease: (newPositions: ElementPositions) => void;
  setIsDragging: (isDragging: boolean) => void;
  maintainAspectRatio?: boolean;
}> = ({
  positions,
  type,
  minimumWidthPercentage = 1,
  outlineRef,
  updatePositions,
  onHandleRelease,
  setIsDragging,
  maintainAspectRatio
}) => {
  const scale = useCurrentScale();
  const size = Math.round(HANDLE_SIZE / scale);
  const borderSize = 1 / scale;

  const sizeStyle: React.CSSProperties = useMemo(() => {
    return {
      position: 'absolute',
      height: size,
      width: size,
      backgroundColor: 'white',
      border: `${borderSize}px solid ${SELECTION_OUTLINE_COLOR}`
    };
  }, [borderSize, size]);

  const margin = -size / 2 - borderSize;

  const style: React.CSSProperties = useMemo(() => {
    if (type === 'top') {
      return {
        ...sizeStyle,
        marginTop: margin,
        left: '50%',
        marginLeft: -size / 2,
        cursor: 'ns-resize'
      };
    }

    if (type === 'top-right') {
      return {
        ...sizeStyle,
        marginTop: margin,
        marginRight: margin,
        right: 0,
        cursor: 'nesw-resize'
      };
    }

    if (type === 'right') {
      return {
        ...sizeStyle,
        marginRight: margin,
        right: 0,
        top: '50%',
        marginTop: -size / 2,
        cursor: 'ew-resize'
      };
    }

    if (type === 'bottom-right') {
      return {
        ...sizeStyle,
        marginBottom: margin,
        marginRight: margin,
        right: 0,
        bottom: 0,
        cursor: 'nwse-resize'
      };
    }

    if (type === 'bottom') {
      return {
        ...sizeStyle,
        marginBottom: margin,
        bottom: 0,
        left: '50%',
        marginLeft: -size / 2,
        cursor: 'ns-resize'
      };
    }

    if (type === 'bottom-left') {
      return {
        ...sizeStyle,
        marginBottom: margin,
        marginLeft: margin,
        bottom: 0,
        cursor: 'nesw-resize'
      };
    }

    if (type === 'left') {
      return {
        ...sizeStyle,
        marginLeft: margin,
        top: '50%',
        marginTop: -size / 2,
        cursor: 'ew-resize'
      };
    }
    if (type === 'top-left') {
      return {
        ...sizeStyle,
        marginLeft: margin,
        marginTop: margin,
        cursor: 'nwse-resize'
      };
    }

    throw new Error('Unknown type: ' + JSON.stringify(type));
  }, [margin, size, sizeStyle, type]);

  const onPointerDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (e.button !== 0) {
        return;
      }

      const initialX = e.clientX;
      const initialY = e.clientY;
      const parentDimensions = outlineRef.current?.parentElement?.getBoundingClientRect();
      const parentHeight = parentDimensions?.height || 0;
      const parentWidth = parentDimensions?.width || 0;

      // Store initial positions
      const initialPositions = {
        top: parseFloat(positions.top as string),
        left: parseFloat(positions.left as string),
        width: parseFloat(positions.width as string),
        height: parseFloat(positions.height as string)
      };

      // Store the original aspect ratio when resize starts
      const originalAspectRatio = maintainAspectRatio ? initialPositions.width / initialPositions.height : null;

      let lastPositions = positions;

      const onPointerMove = (pointerMoveEvent: PointerEvent) => {
        setIsDragging(true);
        const offsetY = pointerMoveEvent.clientY - initialY;
        const offsetX = pointerMoveEvent.clientX - initialX;

        // Calculate relative changes in percentage with sensitivity control
        const relativeChangeInX = (offsetX / parentWidth) * 100;
        const relativeChangeInY = (offsetY / parentHeight) * 100;

        // Determine which edges are being resized
        const isLeft = type === 'top-left' || type === 'left' || type === 'bottom-left';
        const isTop = type === 'top-left' || type === 'top' || type === 'top-right';
        const isRight = type === 'top-right' || type === 'right' || type === 'bottom-right';
        const isBottom = type === 'bottom-left' || type === 'bottom' || type === 'bottom-right';

        let newWidth = parseFloat(positions.width as string);
        let newHeight = parseFloat(positions.height as string);
        let newLeft = parseFloat(positions.left as string);
        let newTop = parseFloat(positions.top as string);

        if (maintainAspectRatio && originalAspectRatio) {
          // Handle aspect ratio maintenance
          if (type === 'top-left' || type === 'top-right' || type === 'bottom-right' || type === 'bottom-left') {
            // Corner resize - properly handle direction and aspect ratio
            let widthChange = 0;
            let heightChange = 0;

            if (type === 'top-left') {
              // For top-left, dragging right/down should increase size
              widthChange = -relativeChangeInX;
              heightChange = -relativeChangeInY;
            } else if (type === 'top-right') {
              // For top-right, dragging left/down should increase size
              widthChange = relativeChangeInX;
              heightChange = -relativeChangeInY;
            } else if (type === 'bottom-right') {
              // For bottom-right, dragging right/down should increase size
              widthChange = relativeChangeInX;
              heightChange = relativeChangeInY;
            } else if (type === 'bottom-left') {
              // For bottom-left, dragging left/up should increase size
              widthChange = -relativeChangeInX;
              heightChange = relativeChangeInY;
            }

            // Use a more stable approach - calculate both and use the one that maintains aspect ratio better
            const newWidthFromWidth = Math.max(minimumWidthPercentage, initialPositions.width + widthChange);
            const newHeightFromWidth = newWidthFromWidth / originalAspectRatio;

            const newHeightFromHeight = Math.max(1, initialPositions.height + heightChange);
            const newWidthFromHeight = newHeightFromHeight * originalAspectRatio;

            // Choose the approach that results in the larger size change
            const sizeFromWidth = newWidthFromWidth * newHeightFromWidth;
            const sizeFromHeight = newWidthFromHeight * newHeightFromHeight;

            if (sizeFromWidth > sizeFromHeight) {
              newWidth = newWidthFromWidth;
              newHeight = newHeightFromWidth;
            } else {
              newWidth = newWidthFromHeight;
              newHeight = newHeightFromHeight;
            }

            // Update position based on which corner is being dragged
            if (type === 'top-left') {
              newLeft = initialPositions.left + (initialPositions.width - newWidth);
              newTop = initialPositions.top + (initialPositions.height - newHeight);
            } else if (type === 'top-right') {
              newTop = initialPositions.top + (initialPositions.height - newHeight);
            } else if (type === 'bottom-left') {
              newLeft = initialPositions.left + (initialPositions.width - newWidth);
            }
            // bottom-right keeps original position
          } else {
            // Edge resize - maintain aspect ratio by adjusting the other dimension
            if (isLeft || isRight) {
              // Horizontal resize
              const widthChange = isLeft ? -relativeChangeInX : relativeChangeInX;
              newWidth = Math.max(minimumWidthPercentage, initialPositions.width + widthChange);
              newHeight = newWidth / originalAspectRatio;

              if (isLeft) {
                newLeft = initialPositions.left + (initialPositions.width - newWidth);
              }
            } else if (isTop || isBottom) {
              // Vertical resize
              const heightChange = isTop ? -relativeChangeInY : relativeChangeInY;
              newHeight = Math.max(1, initialPositions.height + heightChange);
              newWidth = newHeight * originalAspectRatio;

              if (isTop) {
                newTop = initialPositions.top + (initialPositions.height - newHeight);
              }
            }
          }
        } else {
          // Original logic for non-aspect-ratio elements
          if (isLeft) {
            const maxDecrease = newWidth - 1;
            const actualDecrease = Math.min(relativeChangeInX, maxDecrease);
            newWidth -= actualDecrease;
            newLeft += actualDecrease;
          } else if (isRight) {
            const maxIncrease = 100 - newLeft - newWidth;
            newWidth += Math.min(relativeChangeInX, maxIncrease);
          }

          if (isTop) {
            const maxDecrease = newHeight - 1;
            const actualDecrease = Math.min(relativeChangeInY, maxDecrease);
            newHeight -= actualDecrease;
            newTop += actualDecrease;
          } else if (isBottom) {
            const maxIncrease = 100 - newTop - newHeight;
            newHeight += Math.min(relativeChangeInY, maxIncrease);
          }
        }

        if (maintainAspectRatio) {
          // For aspect ratio elements, only ensure minimum dimensions, no boundary constraints
          newWidth = Math.max(minimumWidthPercentage, newWidth);
          newHeight = Math.max(1, newHeight);
        } else {
          // For non-aspect-ratio elements, apply original boundary constraints
          newWidth = Math.max(minimumWidthPercentage || 1, Math.round(newWidth));
          newHeight = Math.max(1, Math.round(newHeight));
          newLeft = Math.max(0, Math.min(99, Math.round(newLeft)));
          newTop = Math.max(0, Math.min(99, Math.round(newTop)));
        }

        const newPositions = {
          top: `${newTop}%`,
          left: `${newLeft}%`,
          width: `${newWidth}%`,
          height: `${newHeight}%`
        };

        lastPositions = newPositions;
        updatePositions(newPositions);
      };

      const onPointerUp = () => {
        setIsDragging(false);
        onHandleRelease(lastPositions);
        window.removeEventListener('pointermove', onPointerMove);
      };

      window.addEventListener('pointermove', onPointerMove, { passive: true });
      window.addEventListener('pointerup', onPointerUp, { once: true });
    },
    [positions, updatePositions, outlineRef, type, setIsDragging, minimumWidthPercentage, maintainAspectRatio]
  );

  return <div onPointerDown={onPointerDown} style={style} />;
};
